import { useEffect, useRef } from 'react';

/**
 * Re-run `refresh` when the user comes back to the screen — no timer.
 *
 * Returning to a browser tab fires BOTH `visibilitychange` and `focus`, so
 * listening to the pair naively costs two identical round-trips every time.
 * Both are still needed (`focus` alone misses a tab switch, `visibilitychange`
 * alone misses returning from another application), so the two are collapsed
 * into one wake signal here.
 *
 * @param {() => void} refresh
 * @param {boolean} enabled
 */
export default function useRefreshOnFocus(refresh, enabled = true) {
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  useEffect(() => {
    if (!enabled) return undefined;
    let lastWake = 0;
    function onWake() {
      if (document.hidden) return;
      const now = Date.now();
      if (now - lastWake < 1000) return;
      lastWake = now;
      refreshRef.current();
    }
    window.addEventListener('focus', onWake);
    document.addEventListener('visibilitychange', onWake);
    return () => {
      window.removeEventListener('focus', onWake);
      document.removeEventListener('visibilitychange', onWake);
    };
    // refreshRef keeps the latest callback without re-registering listeners.
  }, [enabled]);
}
